import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import api from '../services/api';
import { CheckCircle, XCircle, Clock, MapPin, UserCheck } from 'lucide-react';
import './AcceptInvite.css';

interface InviteDetails {
  email: string;
  site: {
    id: string;
    name: string;
    code: string;
  };
  role: string;
  expires_at: string;
}

const AcceptInvite: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [invite, setInvite] = useState<InviteDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [errorStatus, setErrorStatus] = useState('');

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUserEmail, setCurrentUserEmail] = useState('');
  const [currentUserId, setCurrentUserId] = useState('');

  const [showSignup, setShowSignup] = useState(false);
  const [signupPassword, setSignupPassword] = useState('');
  const [signupFullName, setSignupFullName] = useState('');
  const [signingUp, setSigningUp] = useState(false);

  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    checkAuthAndLoadInvite();
  }, [token]);

  const checkAuthAndLoadInvite = async () => {
    try {
      // Check if user is authenticated
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        setIsAuthenticated(true);
        setCurrentUserEmail(session.user.email || '');
        setCurrentUserId(session.user.id);
      }

      // Load invite details
      const response = await api.get(`/api/invites/token/${token}`);
      setInvite(response.data);

      // Pre-fill signup name from email
      if (response.data.email) {
        setSignupFullName(response.data.email.split('@')[0].replace(/[._]/g, ' '));
      }
    } catch (err: any) {
      console.error('Error loading invite:', err);
      setError(err.response?.data?.error || 'Failed to load invite');
      setErrorStatus(err.response?.data?.status || '');
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!invite) return;

    setSigningUp(true);
    setError('');

    try {
      // Create account
      const { data, error: signupError } = await supabase.auth.signUp({
        email: invite.email,
        password: signupPassword,
        options: {
          data: {
            full_name: signupFullName
          }
        }
      });

      if (signupError) throw signupError;

      if (data.user) {
        setIsAuthenticated(true);
        setCurrentUserId(data.user.id);
        setCurrentUserEmail(data.user.email || '');

        // Accept the invite
        await acceptInvite(data.user.id, signupFullName);
      }
    } catch (err: any) {
      console.error('Signup error:', err);
      setError(err.message || 'Failed to create account');
    } finally {
      setSigningUp(false);
    }
  };

  const handleLogin = async () => {
    // Redirect to login with return URL
    navigate(`/login?redirect=/accept-invite/${token}`);
  };

  const handleGoogleSignIn = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/accept-invite/${token}`,
        },
      });

      if (error) throw error;
    } catch (err: any) {
      setError(err.message || 'Error signing in with Google');
    }
  };

  const acceptInvite = async (userId: string, fullName?: string) => {
    setAccepting(true);
    setError('');

    try {
      await api.post(`/api/invites/token/${token}/accept`, {
        user_id: userId,
        full_name: fullName || signupFullName
      });

      setAccepted(true);

      // Redirect to home after 2 seconds
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (err: any) {
      console.error('Error accepting invite:', err);
      setError(err.response?.data?.error || 'Failed to accept invite');
    } finally {
      setAccepting(false);
    }
  };

  const handleAccept = () => {
    if (currentUserId) {
      acceptInvite(currentUserId);
    }
  };

  if (loading) {
    return (
      <div className="accept-invite-container">
        <div className="accept-invite-card">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading invite...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !invite) {
    return (
      <div className="accept-invite-container">
        <div className="accept-invite-card">
          <div className="error-state">
            {errorStatus === 'expired' ? (
              <Clock size={48} className="error-icon expired" />
            ) : (
              <XCircle size={48} className="error-icon" />
            )}
            <h2>{errorStatus === 'expired' ? 'Invite Expired' : 'Invalid Invite'}</h2>
            <p>{error}</p>
            <button onClick={() => navigate('/login')} className="btn-primary">
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (accepted) {
    return (
      <div className="accept-invite-container">
        <div className="accept-invite-card">
          <div className="success-state">
            <CheckCircle size={48} className="success-icon" />
            <h2>Welcome!</h2>
            <p>You now have access to {invite?.site.name}.</p>
            <p className="redirect-notice">Redirecting to your dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="accept-invite-container">
      <div className="accept-invite-card">
        <div className="invite-logo" onClick={() => navigate('/')}>
          <span className="logo-icon">&#9678;</span>
          SiteOperations.io
        </div>
        <div className="invite-header">
          <UserCheck size={32} className="invite-icon" />
          <h1>You're Invited!</h1>
        </div>

        <div className="invite-details">
          <div className="detail-row">
            <MapPin size={18} />
            <div>
              <span className="detail-label">Site</span>
              <span className="detail-value">{invite?.site.name}</span>
            </div>
          </div>
          <div className="detail-row">
            <span className="role-badge">{invite?.role === 'basic_user' ? 'User' : invite?.role}</span>
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}

        {isAuthenticated ? (
          <div className="authenticated-section">
            {currentUserEmail.toLowerCase() === invite?.email.toLowerCase() ? (
              <>
                <p className="auth-status">
                  Signed in as <strong>{currentUserEmail}</strong>
                </p>
                <button
                  onClick={handleAccept}
                  className="btn-primary"
                  disabled={accepting}
                >
                  {accepting ? 'Accepting...' : 'Accept Invitation'}
                </button>
              </>
            ) : (
              <div className="email-mismatch">
                <p>
                  You're signed in as <strong>{currentUserEmail}</strong>, but this invite is for <strong>{invite?.email}</strong>.
                </p>
                <button onClick={() => supabase.auth.signOut()} className="btn-secondary">
                  Sign Out
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="unauthenticated-section">
            <p className="invite-email">This invite is for <strong>{invite?.email}</strong></p>

            {!showSignup ? (
              <div className="auth-options">
                <button onClick={handleGoogleSignIn} className="google-button">
                  <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4"/>
                    <path d="M9.003 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.96v2.332C2.439 15.983 5.485 18 9.003 18z" fill="#34A853"/>
                    <path d="M3.964 10.712c-.18-.54-.282-1.117-.282-1.71 0-.593.102-1.17.282-1.71V4.96H.957C.347 6.175 0 7.55 0 9.002c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
                    <path d="M9.003 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.464.891 11.426 0 9.002 0 5.485 0 2.439 2.017.96 4.958L3.967 7.29c.708-2.127 2.692-3.71 5.036-3.71z" fill="#EA4335"/>
                  </svg>
                  Continue with Google
                </button>

                <div className="auth-divider">
                  <span>or</span>
                </div>

                <button onClick={() => setShowSignup(true)} className="btn-secondary">
                  Create Account with Password
                </button>
                <button onClick={handleLogin} className="btn-link">
                  Already have an account? Sign In
                </button>
              </div>
            ) : (
              <form onSubmit={handleSignup} className="signup-form">
                <div className="form-group">
                  <label>Email</label>
                  <input type="email" value={invite?.email} disabled className="input-disabled" />
                </div>
                <div className="form-group">
                  <label>Full Name</label>
                  <input
                    type="text"
                    value={signupFullName}
                    onChange={(e) => setSignupFullName(e.target.value)}
                    placeholder="Your full name"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Password</label>
                  <input
                    type="password"
                    value={signupPassword}
                    onChange={(e) => setSignupPassword(e.target.value)}
                    placeholder="Create a password"
                    minLength={6}
                    required
                  />
                </div>
                <div className="form-actions">
                  <button type="submit" className="btn-primary" disabled={signingUp}>
                    {signingUp ? 'Creating Account...' : 'Create Account & Accept'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowSignup(false)}
                    className="btn-link"
                  >
                    Back
                  </button>
                </div>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AcceptInvite;
